import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

type OnboardingForm = {
  name: string
  phone: string
  email: string
  address: string
  city: string
  state: string
  country: string
  currency: string
  default_language: string
  main_branch_name: string
}

const initialForm: OnboardingForm = {
  name: '',
  phone: '',
  email: '',
  address: '',
  city: '',
  state: '',
  country: 'Nigeria',
  currency: 'NGN',
  default_language: 'en',
  main_branch_name: '',
}

function BusinessOnboardingPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const isAdditionalBusiness = location.pathname === '/businesses/new'
  const [form, setForm] = useState(initialForm)
  const [loading, setLoading] = useState(false)
  const [checkingWorkspace, setCheckingWorkspace] = useState(true)
  const [error, setError] = useState('')
  const [idempotencyKey] = useState(() => crypto.randomUUID())

  useEffect(() => {
    async function checkWorkspace() {
      const token = localStorage.getItem('aad_access_token')
      if (!token) {
        navigate('/login', { replace: true })
        return
      }

      try {
        const response = await fetch('/api/businesses/my-businesses', {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (response.ok) {
          const data: { businesses: unknown[] } = await response.json()
          if (!isAdditionalBusiness && data.businesses.length > 0) {
            navigate('/dashboard', { replace: true })
          }
          return
        }
        if (response.status !== 404) {
          localStorage.removeItem('aad_access_token')
          navigate('/login', { replace: true })
        }
      } catch {
        setError('Unable to connect to the server. Please try again.')
      } finally {
        setCheckingWorkspace(false)
      }
    }

    checkWorkspace()
  }, [isAdditionalBusiness, navigate])

  function updateField(field: keyof OnboardingForm, value: string) {
    setForm((current) => ({ ...current, [field]: value }))
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError('')
    setLoading(true)

    try {
      const token = localStorage.getItem('aad_access_token')
      if (!token) {
        navigate('/login', { replace: true })
        return
      }

      const response = await fetch(
        isAdditionalBusiness ? '/api/businesses' : '/api/businesses/onboarding',
        {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...(isAdditionalBusiness ? { 'Idempotency-Key': idempotencyKey } : {}),
        },
        body: JSON.stringify(form),
        },
      )
      const data = await response.json()

      if (!response.ok) {
        setError(
          typeof data.detail === 'string'
            ? data.detail
            : 'Unable to create your business workspace.',
        )
        return
      }

      localStorage.setItem('aad_selected_business_id', data.business.id)
      navigate('/dashboard', { replace: true })
    } catch {
      setError('Unable to connect to the server. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (checkingWorkspace) {
    return <main><p>Preparing your workspace...</p></main>
  }

  return (
    <main>
      <section>
        <h1>{isAdditionalBusiness ? 'Create another business' : 'Set up your business'}</h1>
        <p>Create your business and its main branch to get started.</p>

        <form onSubmit={handleSubmit}>
          <div>
            <label htmlFor="name">Business name</label>
            <input id="name" value={form.name} onChange={(event) => updateField('name', event.target.value)} required />
          </div>
          <div>
            <label htmlFor="main_branch_name">Main branch name</label>
            <input id="main_branch_name" value={form.main_branch_name} onChange={(event) => updateField('main_branch_name', event.target.value)} required />
          </div>
          <div>
            <label htmlFor="phone">Business phone</label>
            <input id="phone" type="tel" value={form.phone} onChange={(event) => updateField('phone', event.target.value)} />
          </div>
          <div>
            <label htmlFor="email">Business email</label>
            <input id="email" type="email" value={form.email} onChange={(event) => updateField('email', event.target.value)} />
          </div>
          <div>
            <label htmlFor="address">Address</label>
            <input id="address" value={form.address} onChange={(event) => updateField('address', event.target.value)} />
          </div>
          <div>
            <label htmlFor="city">City</label>
            <input id="city" value={form.city} onChange={(event) => updateField('city', event.target.value)} />
          </div>
          <div>
            <label htmlFor="state">State</label>
            <input id="state" value={form.state} onChange={(event) => updateField('state', event.target.value)} />
          </div>
          <div>
            <label htmlFor="country">Country</label>
            <input id="country" value={form.country} onChange={(event) => updateField('country', event.target.value)} required />
          </div>
          <div>
            <label htmlFor="currency">Currency</label>
            <input id="currency" value={form.currency} onChange={(event) => updateField('currency', event.target.value)} required />
          </div>
          <div>
            <label htmlFor="default_language">Default language</label>
            <select id="default_language" value={form.default_language} onChange={(event) => updateField('default_language', event.target.value)}>
              <option value="en">English</option>
            </select>
          </div>

          {error && <p role="alert">{error}</p>}

          <button type="submit" disabled={loading}>
            {loading ? 'Creating workspace...' : 'Create business workspace'}
          </button>
        </form>
      </section>
    </main>
  )
}

export default BusinessOnboardingPage
